import { useMutation, UseMutationResult, UseMutationOptions, MutationKey } from "@tanstack/react-query";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { Variables, request } from "graphql-request";
import { v4 as uuidv4 } from "uuid";

import { useSafeAuth0GetAccessToken } from "../../../hooks/useSafeAuth0GetAccessToken";

export type GraphQLMutationParams<TMutationResult, TVariables, TError = Error> = {
  url: string;
  mutationKey: MutationKey;
  document: TypedDocumentNode<TMutationResult, TVariables> | string;
  options?: Omit<UseMutationOptions<TMutationResult, TError, TVariables>, "mutationFn">;
  requestHeaders?: Record<string, string>;
  getAccessToken?: () => Promise<string>;
};

// Returns mutation options
export function getGraphQLMutation<TMutationResult, TVariables extends Variables, TError = Error>({
  url,
  mutationKey,
  document,
  options = {},
  requestHeaders = {},
  getAccessToken,
}: GraphQLMutationParams<TMutationResult, TVariables, TError>): UseMutationOptions<
  TMutationResult,
  TError,
  TVariables
> {
  const mutationFn = async (variables: TVariables) => {
    const authToken = getAccessToken ? await getAccessToken() : undefined;

    return request<TMutationResult>({
      url,
      document,
      variables,
      requestHeaders: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        "X-REQUEST-ID": uuidv4(),
        ...requestHeaders,
      },
    });
  };

  return { mutationKey, mutationFn, ...options };
}

export function useGraphQLMutation<TMutationResult, TVariables extends Variables, TError = Error>(
  params: Omit<GraphQLMutationParams<TMutationResult, TVariables, TError>, "getAccessToken">,
): UseMutationResult<TMutationResult, TError, TVariables> {
  const { getAccessTokenSilently } = useSafeAuth0GetAccessToken();
  const mutationOptions = getGraphQLMutation<TMutationResult, TVariables, TError>({
    ...params,
    getAccessToken: getAccessTokenSilently,
  });
  return useMutation<TMutationResult, TError, TVariables>(mutationOptions);
}
